import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useUploadDraft } from "@/contexts/UploadDraftContext";
import { ArrowRight, User, Mail, Lock, ImageIcon } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasDraft } = useUploadDraft();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  usePageMeta({
    title: "Create your free ColourMatch account",
    description: "Sign up free and visualise paint colours on your home in seconds. No credit card needed for your first render.",
    path: "/register",
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(form);
      // Honour ?from= for the staged-upload flow; default to /visualiser/new which
      // consumes the UploadDraftContext if a draft is present.
      const next = searchParams.get("from") || "/visualiser/new";
      nav(next);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-10" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <span className="w-10 h-10 bg-brand-navy rounded-2xl flex items-center justify-center">
              <span className="block w-4 h-4 bg-white rounded-md" />
            </span>
          </Link>
          <h1 className="text-4xl font-bold tracking-tight text-brand-ink mb-2">Create your account</h1>
          <p className="text-brand-muted">It takes ten seconds. Then start visualising.</p>
          {hasDraft && (
            <div
              data-testid="register-photo-ready-hint"
              className="mt-5 mx-auto max-w-sm inline-flex items-center gap-2 bg-brand-teal/10 border border-brand-teal/30 text-brand-ink text-sm px-4 py-2.5 rounded-2xl"
              role="status"
              aria-live="polite"
            >
              <ImageIcon size={16} className="text-brand-teal" aria-hidden="true" />
              <span>Your photo is ready and will continue after you create your account.</span>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {err && (
            <div className="bg-destructive/5 border border-destructive/20 text-destructive rounded-2xl px-4 py-3 text-sm" data-testid="register-error">
              {err}
            </div>
          )}

          <div className="relative">
            <User size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              required value={form.name} onChange={onChange("name")}
              placeholder="Full name"
              data-testid="register-name-input"
              className="w-full h-14 pl-12 pr-5 bg-white border border-brand-border rounded-2xl text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
          </div>
          <div className="relative">
            <Mail size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="email" required value={form.email} onChange={onChange("email")}
              placeholder="Email address"
              data-testid="register-email-input"
              className="w-full h-14 pl-12 pr-5 bg-white border border-brand-border rounded-2xl text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
          </div>
          <div className="relative">
            <Lock size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="password" required minLength={6} value={form.password} onChange={onChange("password")}
              placeholder="Password (min. 6 characters)"
              data-testid="register-password-input"
              className="w-full h-14 pl-12 pr-5 bg-white border border-brand-border rounded-2xl text-base placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
          </div>

          <button
            type="submit" disabled={busy}
            data-testid="register-submit-btn"
            className="w-full h-14 bg-brand-navy text-white rounded-2xl text-base font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? "Creating account…" : <>Create account <ArrowRight size={18} /></>}
          </button>
          <p className="text-center text-xs text-brand-muted" data-testid="register-reassurance">
            Free to start · No credit card needed · First render is free
          </p>
        </form>

        <p className="mt-8 text-center text-sm text-brand-muted">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-ink font-semibold hover:text-brand-teal" data-testid="register-to-login-link">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
